import { AlertCircle } from "lucide-react";
import type { LabelHTMLAttributes, ReactNode } from "react";
import { useId } from "react";
import { Icon } from "../icons/Icon";
import { cx } from "../styles";

/**
 * The three pieces every form control is wrapped in: a label, an optional hint,
 * and an error message.
 *
 * They are one component rather than three loose ones because of the wiring
 * between them. A label needs `htmlFor`; a hint and an error need ids that the
 * control lists in `aria-describedby`; an invalid control needs
 * `aria-invalid`. Every one of those is easy to forget and none of them is
 * visible when it is missing — the screen looks finished and a screen reader
 * announces an unlabelled text box with no explanation of why it is red.
 *
 * So {@link Field} generates the ids and hands them to the control through a
 * render prop. A caller cannot wire it up wrongly because a caller does not
 * wire it up.
 */

/** What a control needs in order to be described by its own label and hint. */
export interface FieldControlProps {
	id: string;
	"aria-describedby": string | undefined;
	"aria-invalid": true | undefined;
}

export interface FieldProps {
	label: ReactNode;
	/** A hint shown under the control, before anything goes wrong. */
	hint?: ReactNode;
	/**
	 * The problem with the current value.
	 *
	 * When set, the control is `aria-invalid` and the message replaces the hint.
	 * Replaces rather than joins it: a field showing both "Must be unique" and
	 * "This SKU already exists" is telling somebody the same thing twice, and
	 * the second one is the one they need.
	 */
	error?: ReactNode;
	/** Marks the control required, visually and to assistive technology. */
	required?: boolean;
	/**
	 * Marks the field optional in its label.
	 *
	 * The honest default in most forms is the opposite of the convention: when
	 * three fields of eleven are optional, an asterisk on eight is noise and the
	 * word "optional" on three is information. Marking both is a form that
	 * shouts, so a caller picks one.
	 */
	optional?: boolean;
	className?: string;
	children: (props: FieldControlProps) => ReactNode;
}

export function Field({ label, hint, error, required, optional, className, children }: FieldProps) {
	const id = useId();
	const hintId = `${id}-hint`;
	const errorId = `${id}-error`;

	const invalid = error !== undefined && error !== null && error !== false;
	const describedBy = invalid ? errorId : hint ? hintId : undefined;

	return (
		<div className={cx("flex flex-col gap-1.5", className)}>
			<Label htmlFor={id} required={required} optional={optional}>
				{label}
			</Label>

			{children({
				id,
				"aria-describedby": describedBy,
				"aria-invalid": invalid || undefined,
			})}

			{invalid ? (
				// `role="alert"` so the message is announced when it appears, which
				// is the moment a server rejection lands on a field somebody has
				// already moved away from.
				<p
					id={errorId}
					role="alert"
					className="flex items-center gap-1.5 text-[11.5px] text-danger-fg"
				>
					{/* The icon is the second signal. A red message is a colour-only
					    cue, and the person who cannot see red is exactly the person a
					    form should not be quietly failing. */}
					<Icon as={AlertCircle} size={14} className="shrink-0" />
					{error}
				</p>
			) : (
				hint && <HelperText id={hintId}>{hint}</HelperText>
			)}
		</div>
	);
}

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
	/** Renders the word "optional" after the label, in the muted weight. */
	optional?: boolean | undefined;
	/** The word itself, translated by the caller. */
	optionalLabel?: string | undefined;
	/**
	 * The id of the control this labels. Required, not optional.
	 *
	 * A `<label>` with nothing to point at is text that looks like a label: it
	 * reads as one, and clicking it focuses nothing. Requiring the association in
	 * the type is what makes that impossible rather than merely discouraged —
	 * `Field` generates the id and passes it, so most callers never write one.
	 */
	htmlFor: string;
	required?: boolean | undefined;
}

/** A form label. */
export function Label({
	htmlFor,
	required,
	optional,
	optionalLabel = "optional",
	className,
	children,
	...rest
}: LabelProps) {
	return (
		// htmlFor is destructured and written out rather than left in the spread
		// so the association is visible to a reader and to the linter — a rule
		// that cannot see through `{...rest}` is a rule that would have to be
		// suppressed here, and suppressing an accessibility rule on a Label is
		// the wrong habit to start.
		<label htmlFor={htmlFor} className={cx("text-xs font-bold text-fg", className)} {...rest}>
			{children}
			{required && (
				// The asterisk is decoration; `aria-required` on the control is what
				// actually says so. Announcing "asterisk" after every label is noise.
				<span aria-hidden="true" className="ms-0.5 text-danger-fg">
					*
				</span>
			)}
			{optional === true && (
				<span className="ms-1 font-medium text-fg-subtle">{optionalLabel}</span>
			)}
		</label>
	);
}

/** Secondary text under a control. */
export function HelperText({
	className,
	children,
	...rest
}: {
	children: ReactNode;
	className?: string;
	id?: string;
}) {
	return (
		<p className={cx("text-[11.5px] leading-relaxed text-fg-muted", className)} {...rest}>
			{children}
		</p>
	);
}
